import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getUserProfile } from '../api/ProfileAPI';
import io from 'socket.io-client';
import React from 'react';
import { path } from '../api/ProfileAPI';
import { Button, Badge } from './ui';
import { useAuth } from '../contexts/AuthContext';
import { useUnread } from '../contexts/UnreadContext';
import TopNav from './TopNav';
import AboutPopover from './AboutPopover';


const Header = props =>{
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, user: authUser, logout: logoutFromContext, demoLogin } = useAuth();
    const { unreadCounts, setUnreadCounts } = useUnread();
    const [loggingOut, setLoggingOut] = useState(false);
    const socketRef = useRef(null);
    const pollIntervalRef = useRef(null);

    async function logOut(){
        // Prevent multiple simultaneous logout attempts
        if (loggingOut) {
            console.log('⚠️ [LOGOUT] Logout already in progress, ignoring click');
            return;
        }
        
        console.log('🔴 [LOGOUT] ============================================');
        console.log('🔴 [LOGOUT] Logout click handler triggered');
        setLoggingOut(true);
        
        try {
            console.log('🔴 [LOGOUT] Starting logout process...');
            
            // Disconnect socket before logout
            if (socketRef.current) {
                console.log('🔴 [LOGOUT] Disconnecting socket...');
                socketRef.current.disconnect();
                socketRef.current = null;
                console.log('🟢 [LOGOUT] Socket disconnected successfully');
            }
            
            // Call logout from context (handles API call and auth state)
            await logoutFromContext();
            
            console.log('🟢 [LOGOUT] Logout complete');
            
            // Navigate to landing page (Welcome to PennThrift!)
            console.log('🔴 [LOGOUT] About to navigate to /');
            navigate('/', { replace: true });
            console.log('🟢 [LOGOUT] Navigate() called - navigation should occur');
        } catch (error) {
            console.error('❌ [LOGOUT] Logout error:', error);
            // Navigate anyway - auth context should have cleared state
            navigate('/', { replace: true });
        } finally {
            console.log('🔴 [LOGOUT] Finally block executing');
            setLoggingOut(false);
            console.log('🟢 [LOGOUT] loggingOut state:', false);
            console.log('🔴 [LOGOUT] ============================================');
        }
    }

    // Check if concierge conversation has been opened in this demo session
    const hasOpenedConcierge = () => {
        if (typeof window === 'undefined') return false;
        const isDemoUser = authUser?.username === 'demo' || authUser?.isDemo === true;
        if (!isDemoUser) return false;
        
        // Ensure demoSessionId exists (generate if missing)
        let sessionId = sessionStorage.getItem('demoSessionId');
        if (!sessionId) {
            // Generate UUID v4
            sessionId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
                const r = Math.random() * 16 | 0;
                const v = c === 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            });
            sessionStorage.setItem('demoSessionId', sessionId);
        }
        
        return localStorage.getItem(`demoConciergeOpened:${sessionId}`) === '1';
    };
    
    // Fetch unread from server and update context (v2 - clean rewrite)
    const fetchUnread = async () => {
        if (!authUser?.username) return;

        const isDemoUser = authUser?.username === 'demo' || authUser?.isDemo === true;
        if (isDemoUser) {
            setUnreadCounts(hasOpenedConcierge() ? [] : ['concierge']);
            return;
        }

        try {
            const profile = await getUserProfile(authUser.username);
            const unread = Array.isArray(profile?.unread)
                ? profile.unread.map(id => String(id))
                : [];
            setUnreadCounts(unread);
        } catch (e) {
            console.error('Error fetching unread:', e);
        }
    };

    // Initialize socket on /api/messages namespace so we receive unread events
    useEffect(() => {
        if (typeof window !== 'undefined' && path && isAuthenticated && authUser) {
            try {
                if (!socketRef.current) {
                    const socketUrl = path.replace(/\/$/, '');
                    socketRef.current = io(`${socketUrl}/api/messages`, {
                        path: '/socket.io',
                        withCredentials: true,
                        transports: ['websocket', 'polling'],
                        reconnection: true,
                        reconnectionAttempts: 5,
                        reconnectionDelay: 1000,
                        reconnectionDelayMax: 5000,
                        timeout: 20000
                    });
                    
                    socketRef.current.on('connect', () => {
                        console.log('✅ Socket.io connected to /api/messages (Header)');
                        fetchUnread();
                    });
                    
                    socketRef.current.on('unread', () => {
                        fetchUnread();
                    });
                    
                    socketRef.current.on('connect_error', (error) => {
                        console.error('❌ Socket.io connection error (Header):', error);
                    });
                }

                // Polling fallback: refetch every 30s in case socket events are missed
                if (!pollIntervalRef.current) {
                    pollIntervalRef.current = setInterval(fetchUnread, 30000);
                }
            } catch (e) {
                console.error('Error initializing socket:', e);
            }
        } else {
            if (socketRef.current) {
                socketRef.current.disconnect();
                socketRef.current = null;
            }
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
        }
        
        return () => {
            if (socketRef.current) {
                socketRef.current.disconnect();
                socketRef.current = null;
            }
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
        };
    }, [isAuthenticated, authUser?.username])

    // Clear unread on logout
    useEffect(() => {
        if (!isAuthenticated) {
            setUnreadCounts([]);
        }
    }, [isAuthenticated, setUnreadCounts]);

    return(
        <header 
            data-testid="header" 
            className="w-full bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50"
        >
            <div className="container max-w-7xl mx-auto px-4">
                <div className="flex items-center justify-between h-24 gap-4">
                    {/* Left side - Brand lockup */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                        <AboutPopover trigger="hover">
                            <div className="flex items-center gap-3 cursor-pointer">
                                <img 
                                    src={require('../assets/logo.png')} 
                                    alt="PennThrift logo" 
                                    className="block h-10 w-10 object-contain"
                                    style={{ transform: 'translateY(1px)' }}
                                />
                                <div className="inline-flex items-center gap-1.5">
                                    <Link 
                                        to={isAuthenticated ? "/store" : "/"} 
                                        className="inline-block text-3xl font-semibold text-gray-900 hover:opacity-80 transition-opacity hidden sm:inline"
                                        style={{ lineHeight: '1.1' }}
                                        aria-label="PennThrift home"
                                    >
                                        PennThrift
                                    </Link>
                                    <button
                                        className="inline-block text-gray-500 hover:text-gray-700 transition-colors cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]"
                                        aria-label="About PennThrift"
                                        style={{ 
                                            fontSize: '14px', 
                                            lineHeight: '1',
                                            opacity: '0.65',
                                            padding: 0,
                                            border: 'none',
                                            background: 'none',
                                            transform: 'translate(4px, 1px)'
                                        }}
                                        type="button"
                                    >
                                        ⓘ
                                    </button>
                                </div>
                            </div>
                        </AboutPopover>
                    </div>

                    {/* Right side - Navigation */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {isAuthenticated && authUser ? (
                            <>
                                {authUser.username === 'demo' && (
                                    <Badge variant="primary" className="mr-2">
                                        Demo Quaker
                                    </Badge>
                                )}
                                <TopNav onLogout={logOut} />
                            </>
                        ) : (
                            <>
                                <Button 
                                    variant="primary" 
                                    className="text-sm"
                                    onClick={async () => {
                                        try {
                                            await demoLogin();
                                            // Auth context will update automatically
                                        } catch (error) {
                                            console.error('Demo login failed:', error);
                                        }
                                    }}
                                >
                                    Try Demo
                                </Button>
                                <Link to="/login">
                                    <Button variant="secondary" className="text-sm">
                                        Log in
                                    </Button>
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </header>
    )

}

export default Header;